/**
 * Tablas de referencia de los artículos.
 *
 * Se generan desde `fiscal.js`, no se escriben a mano. Así la tabla que
 * lee la persona y el número que usa la calculadora no pueden
 * contradecirse, que es exactamente lo que pasaba antes con la UMA.
 */

import {
  TARIFA_ISR_MENSUAL,
  TASAS_INFONAVIT,
  TASAS_IVA,
  CUOTAS_IMSS_OBRERO,
  UMA,
  diasVacacionesPorAntiguedad,
} from "./fiscal.js";
import { CATEGORIAS_MEDIDA } from "./medidas.js";
import { formatoMXN } from "../lib/calc.js";

const pesos = (n) => formatoMXN(n);

export const TABLAS = {
  isr: () => ({
    titulo: "Tarifa mensual del ISR (art. 96 LISR)",
    columnas: ["Límite inferior", "Cuota fija", "% sobre excedente"],
    filas: TARIFA_ISR_MENSUAL.map((t) => [
      pesos(t.limiteInferior),
      pesos(t.cuotaFija),
      `${t.tasa}%`,
    ]),
    nota: "Se ubica tu ingreso en el tramo, se toma la cuota fija y se aplica el porcentaje sólo sobre lo que excede el límite inferior.",
  }),

  vacaciones: () => ({
    titulo: "Días de vacaciones por antigüedad (LFT art. 76)",
    columnas: ["Año de servicio", "Días de vacaciones"],
    filas: [1, 2, 3, 4, 5, 6, 11, 16, 21, 26].map((a) => [
      a <= 5 ? `${a}º año` : `${a} años o más`,
      `${diasVacacionesPorAntiguedad(a)} días`,
    ]),
    nota: "Vigente desde la reforma «Vacaciones Dignas» de enero de 2023, que duplicó el mínimo del primer año.",
  }),

  imss: () => ({
    titulo: "Cuotas obrero del IMSS sobre el SBC",
    columnas: ["Concepto", "% del SBC"],
    filas: Object.values(CUOTAS_IMSS_OBRERO).map((c) => [
      c.concepto,
      `${c.tasa}%${c.umasExentas ? ` (sobre el excedente de ${c.umasExentas} UMA)` : ""}`,
    ]),
    nota: `Se calculan sobre el Salario Base de Cotización diario y se mensualizan. UMA diaria vigente: ${pesos(UMA.diaria)}.`,
  }),

  infonavit: () => ({
    titulo: "Tasa y descuento máximo según tu ingreso mensual",
    columnas: ["Ingreso mensual hasta", "Tasa anual", "Descuento máx."],
    filas: TASAS_INFONAVIT.map((t) => [
      Number.isFinite(t.ingresoHasta) ? pesos(t.ingresoHasta) : "Sin límite",
      `${t.tasaAnual}%`,
      `${t.descuentoMaximoPct}%`,
    ]),
    nota: "El descuento máximo es el tope que el instituto puede retener de tu nómina cada mes.",
  }),

  iva: () => ({
    titulo: "Tasas de IVA por región",
    columnas: ["Región", "Tasa", "Divide entre"],
    filas: TASAS_IVA.map((t) => [t.etiqueta, `${t.valor}%`, (1 + t.valor / 100).toFixed(2)]),
    nota: "La última columna es el divisor para desglosar el IVA de un precio que ya lo incluye.",
  }),

  imc: () => ({
    titulo: "Clasificación del IMC según la OMS",
    columnas: ["IMC", "Clasificación"],
    filas: [
      ["Menos de 18.5", "Bajo peso"],
      ["18.5 – 24.9", "Peso normal"],
      ["25.0 – 29.9", "Sobrepeso"],
      ["30.0 o más", "Obesidad"],
    ],
    nota: "Indicador poblacional: no distingue músculo de grasa ni ajusta por edad, sexo o complexión.",
  }),

  medidas: () => ({
    titulo: "Equivalencias exactas más consultadas",
    columnas: ["Unidad", "Equivale a"],
    filas: [
      ["1 pulgada", "2.54 cm exactos"],
      ["1 pie", "30.48 cm"],
      ["1 milla", "1.609344 km"],
      ["1 libra", "453.59237 g"],
      ["1 onza", "28.349523125 g"],
      ["1 galón (EE. UU.)", "3.78541 L"],
      ["1 hectárea", "10,000 m²"],
      ["1 acre", "4,046.86 m²"],
    ],
    nota: "Son definiciones oficiales, no aproximaciones.",
  }),

  rendimiento: () => ({
    titulo: "Rendimiento de referencia en carretera",
    columnas: ["Tipo de vehículo", "km por litro"],
    filas: [
      ["Compacto / sedán chico", "13 – 16"],
      ["Sedán mediano", "11 – 14"],
      ["SUV", "9 – 12"],
      ["Pickup", "7 – 10"],
      ["Camioneta de carga", "5 – 8"],
    ],
    nota: "Baja con velocidad alta, aire acondicionado, carga y pendientes. Si vas cargado o a más de 110 km/h, calcula con el extremo bajo del rango.",
  }),

  presupuesto: () => ({
    titulo: "Reparto 50/30/20 según tu ingreso",
    columnas: ["Ingreso mensual", "Necesidades", "Deseos", "Ahorro"],
    filas: [12000, 18000, 25000, 35000, 50000].map((i) => [
      pesos(i),
      pesos(i * 0.5),
      pesos(i * 0.3),
      pesos(i * 0.2),
    ]),
    nota: "Sobre el ingreso neto, es decir, lo que realmente te llega a la cuenta.",
  }),

  interes: () => ({
    titulo: "Cuánto tarda en duplicarse tu dinero (regla del 72)",
    columnas: ["Tasa anual", "Años aproximados"],
    filas: [4, 6, 8, 10, 12, 15, 20].map((t) => [`${t}%`, (72 / t).toFixed(1)]),
    nota: "Aproximación rápida: 72 entre la tasa. Funciona igual para una deuda, y ahí duele.",
  }),

  volumen: () => ({
    titulo: "Unidades de volumen más usadas en cocina",
    columnas: ["Unidad", "En litros"],
    filas: CATEGORIAS_MEDIDA.volume.units.map((u) => [u.label, `${u.factor} L`]),
  }),
};

/** Ejemplos resueltos con números reales. */
export const EJEMPLOS = {
  tool_iva: {
    titulo: "Ejemplo resuelto",
    pasos: [
      ["Te cobran un servicio de", "$4,640.00 con IVA incluido"],
      ["Divides entre 1.16", "$4,640 ÷ 1.16 = $4,000.00"],
      ["El subtotal es", "$4,000.00"],
      ["El IVA fue", "$4,640 − $4,000 = $640.00"],
    ],
    cierre: "Si en vez de dividir hubieras restado el 16%, habrías obtenido $3,897.60: un error de $102.40.",
  },
  tool_sueldo: {
    titulo: "Ejemplo resuelto: sueldo de $18,000",
    pasos: [
      ["Sueldo bruto mensual", "$18,000.00"],
      ["Tramo del ISR", "17.92% sobre el excedente de $15,510.42"],
      ["ISR retenido", "$1,417.82 + ($2,489.58 × 17.92%) = $1,863.95"],
      ["Cuotas obrero del IMSS", "$483.13"],
      ["Sueldo neto", "$15,652.91"],
    ],
    cierre: "La retención efectiva es del 13% del bruto, no del 17.92%: el porcentaje del tramo sólo aplica a la parte que excede el límite inferior.",
  },
  tool_infonavit: {
    titulo: "Ejemplo resuelto: crédito de $600,000 a 20 años",
    pasos: [
      ["Tasa según un ingreso de $18,000", "10.45% anual"],
      ["Pago mensual", "$5,970.14"],
      ["Descuento máximo permitido (30%)", "$5,400.00"],
      ["Resultado", "El pago rebasa el tope: habría que alargar el plazo o bajar el monto"],
    ],
    cierre: "Por eso conviene simular antes de ir a la ventanilla: el tope de descuento, y no el precio de la casa, es lo que suele definir cuánto te prestan.",
  },
  tool_descuentos: {
    titulo: "Ejemplo resuelto: 20% + 10%",
    pasos: [
      ["Precio original", "$1,000.00"],
      ["Primer descuento (20%)", "−$200.00 → $800.00"],
      ["Segundo descuento (10%) sobre $800", "−$80.00 → $720.00"],
      ["Descuento efectivo real", "28%, no 30%"],
    ],
    cierre: "Los descuentos encadenados nunca se suman: el segundo se aplica sobre el precio ya rebajado.",
  },
  tool_negocio: {
    titulo: "Ejemplo resuelto: margen contra markup",
    pasos: [
      ["Costo del producto", "$100.00"],
      ["Precio de venta", "$150.00"],
      ["Markup (sobre el costo)", "$50 ÷ $100 = 50%"],
      ["Margen (sobre la venta)", "$50 ÷ $150 = 33.3%"],
    ],
    cierre: "Creer que ganas 50% cuando ganas 33% es el error de precios más caro que existe: subestimas cuánto necesitas vender para cubrir tus gastos fijos.",
  },
  tool_interes: {
    titulo: "Ejemplo resuelto: $10,000 al 10% durante 5 años",
    pasos: [
      ["Con interés simple", "$10,000 + $5,000 = $15,000.00"],
      ["Con interés compuesto anual", "$16,105.10"],
      ["Con capitalización mensual", "$16,453.09"],
    ],
    cierre: "Misma tasa, mismo plazo: $1,453 de diferencia sólo por cómo se capitalizan los intereses.",
  },
  tool_freelance: {
    titulo: "Ejemplo resuelto",
    pasos: [
      ["Quieres que te queden limpios", "$30,000 al mes"],
      ["Gastos operativos", "$5,000 al mes"],
      ["ISR estimado", "30%"],
      ["Necesitas facturar", "$50,000 al mes"],
      ["Horas facturables al año (70% de 40 h/sem)", "1,344 h"],
      ["Tarifa por hora", "$446.43"],
    ],
    cierre: "Dividir $30,000 entre 160 horas habría dado $187 por hora: menos de la mitad de lo que realmente necesitas cobrar.",
  },
  tool_viaje: {
    titulo: "Ejemplo resuelto: CDMX – Guadalajara",
    pasos: [
      ["Distancia aproximada", "540 km"],
      ["Rendimiento de un compacto", "14 km/L"],
      ["Litros necesarios", "38.6 L"],
      ["Gasolina a $24/L", "$925.71"],
      ["Casetas aproximadas", "$780.00"],
      ["Total, entre 4 personas", "$426.43 cada uno"],
    ],
    cierre: "Las casetas son casi la mitad del costo. En rutas largas conviene compararlas contra la ruta libre antes de salir.",
  },
};

/** Qué tablas acompañan a cada herramienta. */
export const TABLAS_POR_HERRAMIENTA = {
  tool_iva: ["iva"],
  tool_sueldo: ["isr", "vacaciones", "imss"],
  tool_infonavit: ["infonavit"],
  tool_interes: ["interes"],
  tool_presupuesto: ["presupuesto"],
  tool_imc: ["imc"],
  tool_medidas: ["medidas", "volumen"],
  tool_viaje: ["rendimiento"],
};
